import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import type { IContact } from '../../../extViewmodels';
import { getDisplayName, getPrincipalInitials } from '../../../models/NameUtils';
import type { ILeadSearchView } from '../../viewModels/leads/interfaces';

/**
 * Compact (~80-90px tall) result row used by the focused-view drawer's
 * `SearchPanel`. The previous dialog-mode search rendered each result
 * with `<LeadCard>`/`<CardView>`, a 600+ px wide horizontal layout that
 * overflowed the 400px-wide drawer. This row stacks the same
 * information into three short rows so it fits the drawer column.
 *
 * Layout:
 *   1. Company name (bold) + website (muted, optional).
 *   2. Avatar (28px) + contact name + email (optional).
 *   3. "Last called: <date> · <colored dot> <outcome>" (optional).
 *
 * Rows 2 and 3 are skipped when their underlying data is missing so a
 * stub lead with only a company name still renders cleanly.
 */
export type SearchResultRowProps = {
	lead: ILeadSearchView;
	onClick: () => void;
};

export const SearchResultRow: FC<SearchResultRowProps> = ({ lead, onClick }) => {
	const company = lead.company;
	const contact = primaryContact(lead);
	const lastInteraction = lead.lastInteraction;
	const outcome = lastInteraction?.outcome;

	return (
		<button type='button' onClick={onClick} className={css(styles.card)}>
			<div className={css(styles.top)}>
				<span className={css(styles.companyName)}>{company?.companyName ?? '(unnamed)'}</span>
				{company?.webSite && <span className={css(styles.companyWebsite)}>{company.webSite}</span>}
			</div>
			{contact && (
				<div className={css(styles.middle)}>
					<ContactAvatar contact={contact} />
					<div className={css(styles.contactInfo)}>
						<span className={css(styles.contactName)}>{getDisplayName(contact) || '(no name)'}</span>
						{contactEmail(contact) && <span className={css(styles.contactEmail)}>{contactEmail(contact)}</span>}
					</div>
				</div>
			)}
			{lastInteraction && (
				<div className={css(styles.bottom)}>
					<span>Last called: {formatDate(lastInteraction.date)}</span>
					{outcome?.name && (
						<>
							<span className={css(styles.bullet)}>·</span>
							<span className={css(styles.outcomeDot)} style={{ background: outcome.hexColor || 'var(--ink-muted)' }} />
							<span>{outcome.name}</span>
						</>
					)}
				</div>
			)}
		</button>
	);
};

function primaryContact(lead: ILeadSearchView): IContact | null {
	return lead.contactReferences?.[0] ?? null;
}

/**
 * `IContact.primaryEmail` is an `EmailAddress` object (`{ value, label }`),
 * not a bare string. Fall back to the first entry in `emailAddresses` to
 * match the precedence used by `CardableContact`.
 */
function contactEmail(contact: IContact): string | undefined {
	if (contact.primaryEmail?.value) return contact.primaryEmail.value;
	const first = contact.emailAddresses?.find(e => !!e?.value);
	return first?.value;
}

function formatDate(iso: string | undefined): string {
	if (!iso) return '';
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return '';
	return d.toLocaleDateString();
}

/**
 * Lightweight avatar that prefers `profilePic` and falls back to
 * initials. We can't reuse `web/components/Avatar` here because it
 * pulls MobX `inject`/`observer` and a `userSession` dependency that
 * isn't worth wiring through for a 28px circle inside a search row.
 */
const ContactAvatar: FC<{ contact: IContact }> = ({ contact }) => {
	const initials = getPrincipalInitials(contact).trim().toUpperCase();
	if (contact.profilePic) {
		return <img className={css(styles.avatar)} src={contact.profilePic} alt='' aria-hidden />;
	}
	return (
		<span className={css(styles.avatar, styles.avatarInitials)} aria-hidden>
			{initials || '?'}
		</span>
	);
};

const styles = StyleSheet.create({
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
		width: '100%',
		padding: '12px 14px',
		// `--bg` reads slightly brighter than the panel's `--bg-elev`
		// background so each row stands out against the surrounding
		// drawer surface.
		background: 'var(--bg)',
		border: '1px solid var(--border)',
		borderRadius: 10,
		cursor: 'pointer',
		textAlign: 'left',
		color: 'var(--ink)',
		font: 'inherit',
		transition: 'background 120ms, border-color 120ms',
		':hover': {
			background: 'var(--hover)',
			borderColor: 'var(--ink-muted)',
		},
	},
	top: {
		display: 'flex',
		alignItems: 'baseline',
		gap: 8,
		minWidth: 0,
	},
	companyName: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	companyWebsite: {
		fontSize: 11,
		color: 'var(--ink-faint)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: 140,
	},
	middle: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		minWidth: 0,
	},
	avatar: {
		width: 28,
		height: 28,
		borderRadius: 999,
		flexShrink: 0,
		objectFit: 'cover',
	},
	avatarInitials: {
		background: 'var(--accent-soft, var(--bg-elev))',
		color: 'var(--accent, var(--ink))',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 11,
		fontWeight: 700,
	},
	contactInfo: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		flex: 1,
	},
	contactName: {
		fontSize: 13,
		color: 'var(--ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	contactEmail: {
		fontSize: 11,
		color: 'var(--ink-muted)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	bottom: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 11,
		color: 'var(--ink-muted)',
	},
	bullet: { color: 'var(--ink-faint)' },
	outcomeDot: {
		width: 8,
		height: 8,
		borderRadius: 999,
		display: 'inline-block',
		flexShrink: 0,
	},
});
